import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { filenameFromContentDisposition } from '@/lib/download';
import { queryKeys } from '@/lib/query-client';
import type {
  AcceptAllUndoBatch,
  AcceptProposalResult,
  AssessmentProposal,
  ComplianceAlert,
  ComplianceAlertFilters,
  ComplianceDashboardData,
  ComplianceDeadline,
  ComplianceRefreshResult,
  ComplianceSummaryData,
  UndoAcceptAllResult,
} from '@/lib/types';

const FIVE_MINUTES = 1000 * 60 * 5;

// ─── Dashboard ──────────────────────────────────────────────────────────────

export function useComplianceDashboard() {
  return useQuery({
    queryKey: queryKeys.compliance.dashboard(),
    queryFn: async (): Promise<ComplianceDashboardData> => {
      const response = await apiClient.get<ComplianceDashboardData>(
        '/api/v1/compliance/dashboard/',
      );
      return response.data;
    },
    staleTime: FIVE_MINUTES,
  });
}

export function useRefreshAssessment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<ComplianceRefreshResult> => {
      const response = await apiClient.post<ComplianceRefreshResult>(
        '/api/v1/compliance/dashboard/refresh/',
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.compliance.pendingProposal() });
      qc.invalidateQueries({ queryKey: queryKeys.compliance.dashboard() });
      qc.invalidateQueries({ queryKey: queryKeys.compliance.alerts() });
      qc.invalidateQueries({ queryKey: queryKeys.compliance.urgentActions() });
    },
  });
}

// ─── Proposal review (S04) ──────────────────────────────────────────────────

export function usePendingProposal() {
  return useQuery({
    queryKey: queryKeys.compliance.pendingProposal(),
    queryFn: async (): Promise<AssessmentProposal | null> => {
      const response = await apiClient.get(
        '/api/v1/compliance/dashboard/pending-proposal/',
        { validateStatus: (s: number) => s === 200 || s === 204 },
      );
      if (response.status === 204) return null;
      return response.data as AssessmentProposal;
    },
    staleTime: FIVE_MINUTES,
  });
}

export function useAcceptProposal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      proposalId,
      clauseRefs,
    }: {
      proposalId: number;
      clauseRefs: string[] | null;
    }): Promise<AcceptProposalResult> => {
      const response = await apiClient.post<AcceptProposalResult>(
        '/api/v1/compliance/dashboard/accept-proposal/',
        { proposal_id: proposalId, clause_refs: clauseRefs },
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.compliance.pendingProposal() });
      qc.invalidateQueries({ queryKey: queryKeys.compliance.acceptAllUndoBatches() });
      qc.invalidateQueries({ queryKey: queryKeys.compliance.dashboard() });
      qc.invalidateQueries({ queryKey: queryKeys.compliance.summary() });
      qc.invalidateQueries({ queryKey: queryKeys.compliance.alerts() });
      qc.invalidateQueries({ queryKey: queryKeys.compliance.urgentActions() });
      qc.invalidateQueries({ queryKey: queryKeys.compliance.upcomingDeadlines() });
      // Accepting a proposal may create CAPAs and update clause statuses
      qc.invalidateQueries({ queryKey: queryKeys.correctiveActions.all });
      qc.invalidateQueries({ queryKey: queryKeys.clauseStatus.all });
    },
  });
}

export function useAcceptAllUndoBatches() {
  return useQuery({
    queryKey: queryKeys.compliance.acceptAllUndoBatches(),
    queryFn: async (): Promise<AcceptAllUndoBatch[]> => {
      const response = await apiClient.get<AcceptAllUndoBatch[]>(
        '/api/v1/compliance/dashboard/accept-all-undo-batches/',
      );
      return response.data;
    },
  });
}

export function useUndoAcceptAll() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (batchId: number): Promise<UndoAcceptAllResult> => {
      const response = await apiClient.post<UndoAcceptAllResult>(
        '/api/v1/compliance/dashboard/undo-accept-all/',
        { batch_id: batchId },
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.compliance.acceptAllUndoBatches() });
      qc.invalidateQueries({ queryKey: queryKeys.compliance.dashboard() });
      qc.invalidateQueries({ queryKey: queryKeys.compliance.summary() });
      qc.invalidateQueries({ queryKey: queryKeys.compliance.alerts() });
      qc.invalidateQueries({ queryKey: queryKeys.compliance.urgentActions() });
      qc.invalidateQueries({ queryKey: queryKeys.compliance.upcomingDeadlines() });
      qc.invalidateQueries({ queryKey: queryKeys.correctiveActions.all });
      qc.invalidateQueries({ queryKey: queryKeys.clauseStatus.all });
    },
  });
}

export function useDiscardProposal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (proposalId: number): Promise<{ status: string; proposal_id: number }> => {
      const response = await apiClient.post<{ status: string; proposal_id: number }>(
        '/api/v1/compliance/dashboard/discard-proposal/',
        { proposal_id: proposalId },
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.compliance.pendingProposal() });
    },
  });
}

// ─── Landing page endpoints (C09) ───────────────────────────────────────────

export function useComplianceSummary() {
  return useQuery({
    queryKey: queryKeys.compliance.summary(),
    queryFn: async (): Promise<ComplianceSummaryData> => {
      const response = await apiClient.get<ComplianceSummaryData>(
        '/api/v1/compliance/dashboard/summary/',
      );
      return response.data;
    },
    staleTime: FIVE_MINUTES,
  });
}

export function useUrgentActions() {
  return useQuery({
    queryKey: queryKeys.compliance.urgentActions(),
    queryFn: async (): Promise<ComplianceAlert[]> => {
      const response = await apiClient.get<ComplianceAlert[]>(
        '/api/v1/compliance/dashboard/urgent-actions/',
      );
      return response.data;
    },
    staleTime: FIVE_MINUTES,
  });
}

export function useUpcomingDeadlines() {
  return useQuery({
    queryKey: queryKeys.compliance.upcomingDeadlines(),
    queryFn: async (): Promise<ComplianceDeadline[]> => {
      const response = await apiClient.get<ComplianceDeadline[]>(
        '/api/v1/compliance/dashboard/upcoming-deadlines/',
      );
      return response.data;
    },
    staleTime: FIVE_MINUTES,
  });
}

// ─── Alerts ─────────────────────────────────────────────────────────────────

function cleanFilters(filters: Record<string, string | undefined>): Record<string, string> {
  const cleaned: Record<string, string> = {};
  for (const [key, value] of Object.entries(filters)) {
    if (value !== undefined && value !== '') {
      cleaned[key] = value;
    }
  }
  return cleaned;
}

export function useComplianceAlerts(filters: ComplianceAlertFilters = {}) {
  const cleaned = cleanFilters(filters as Record<string, string | undefined>);
  return useQuery({
    queryKey: queryKeys.compliance.alertsList(cleaned),
    queryFn: async (): Promise<ComplianceAlert[]> => {
      const response = await apiClient.get<{ results: ComplianceAlert[] } | ComplianceAlert[]>(
        '/api/v1/compliance/alerts/',
        { params: cleaned },
      );
      const data = response.data;
      if (Array.isArray(data)) return data;
      if (data && Array.isArray((data as { results: ComplianceAlert[] }).results)) return (data as { results: ComplianceAlert[] }).results;
      return [];
    },
    staleTime: FIVE_MINUTES,
  });
}

// ─── PDF Downloads (C10) ───────────────────────────────────────────────────

export function useDownloadGapAnalysisPDF() {
  const download = async (): Promise<void> => {
    const response = await apiClient.get('/api/v1/compliance/reports/gap-analysis/pdf/', {
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(response.data as Blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filenameFromContentDisposition(
      response.headers['content-disposition'],
      'GapAnalysis.pdf',
    );
    a.click();
    window.URL.revokeObjectURL(url);
  };
  return { download };
}

export function useDownloadEvidencePackPDF() {
  const download = async (clauseStatusId: number): Promise<void> => {
    const response = await apiClient.get(
      `/api/v1/compliance/clauses/${clauseStatusId}/evidence-pack/pdf/`,
      {
        responseType: 'blob',
      },
    );
    const url = window.URL.createObjectURL(response.data as Blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filenameFromContentDisposition(
      response.headers['content-disposition'],
      `EvidencePack-${clauseStatusId}.pdf`,
    );
    a.click();
    window.URL.revokeObjectURL(url);
  };
  return { download };
}

export function useResolveAlert() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<ComplianceAlert> => {
      const response = await apiClient.post<ComplianceAlert>(
        `/api/v1/compliance/alerts/${id}/resolve/`,
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.compliance.alerts() });
      qc.invalidateQueries({ queryKey: queryKeys.compliance.dashboard() });
    },
  });
}
